const COMPANION_ORIGIN = "http://127.0.0.1:43115";
const TOKEN_KEY = "jarlab-companion-token-v1";

export interface CompanionHealth {
  available: boolean;
  paired: boolean;
  docker: boolean;
  imageReady: boolean;
  recoverator: boolean;
  version?: string;
}

export interface DynamicSandboxResult {
  engine: "local-docker";
  image: string;
  exitCode: number | null;
  timedOut: boolean;
  durationMs: number;
  stdout: string;
  stderr: string;
  filesystemChanges: string[];
  command: string[];
  limits: {
    network: "disabled";
    memoryMb: number;
    cpus: number;
    pids: number;
    timeoutSeconds: number;
  };
}

function token() {
  if (typeof window === "undefined") return "";
  return sessionStorage.getItem(TOKEN_KEY) ?? "";
}

export function saveCompanionToken(value: string) {
  if (typeof window === "undefined") return;
  const clean = value.trim();
  if (clean) sessionStorage.setItem(TOKEN_KEY, clean);
  else sessionStorage.removeItem(TOKEN_KEY);
}

async function companionFetch(path: string, init?: RequestInit) {
  const headers = new Headers(init?.headers);
  const pairingToken = token();
  if (pairingToken) headers.set("x-jarlab-token", pairingToken);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 180_000);
  try {
    return await fetch(`${COMPANION_ORIGIN}${path}`, {
      ...init,
      headers,
      signal: init?.signal ?? controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }
}

export async function getCompanionHealth(): Promise<CompanionHealth> {
  try {
    const response = await companionFetch("/v1/health");
    if (!response.ok) throw new Error("Companion unavailable");
    const health = (await response.json()) as Omit<CompanionHealth, "available">;
    return { ...health, available: true };
  } catch {
    return {
      available: false,
      paired: false,
      docker: false,
      imageReady: false,
      recoverator: false,
    };
  }
}

export async function runCompanionDeobfuscation(file: File) {
  const response = await companionFetch("/v1/deobfuscate", {
    method: "POST",
    headers: {
      "content-type": "application/java-archive",
      "x-jarlab-filename": file.name,
    },
    body: file,
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new Error(
      payload?.error ?? `Recoverator failed with status ${response.status}.`,
    );
  }
  return {
    bytes: new Uint8Array(await response.arrayBuffer()),
    fileName:
      response.headers.get("x-jarlab-filename") ??
      file.name.replace(/\.jar$/i, "-recoverator.jar"),
    engine: response.headers.get("x-jarlab-engine") ?? "Recoverator",
  };
}

export async function runLocalDynamicAnalysis(file: File) {
  const response = await companionFetch("/v1/dynamic", {
    method: "POST",
    headers: {
      "content-type": "application/java-archive",
      "x-jarlab-filename": file.name,
    },
    body: file,
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(
      payload?.error ?? `Local sandbox failed with status ${response.status}.`,
    );
  }
  return payload as DynamicSandboxResult;
}
